import React, { useState } from "react";
import { Btn } from "../ui";

import { BRAND } from "../brand";

const fmtMoney = (n, cur = "USD") =>
  n != null
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: cur,
        minimumFractionDigits: 2,
      }).format(n)
    : "–";

const fmtD = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const REFUND_STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-500",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
};

const REFUND_STATUS_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
};

export default function RefundDetailPanel({
  refund,
  onClose,
  onEdit,
  onSend,
  onStatusChange,
  onAddTask,
  onDelete,
  tasks = [],
  actionSaving,
}) {
  const [printMode, setPrintMode] = useState(false);

  const handleDownload = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 300);
  };

  if (printMode) return <div className="p-8">Print view placeholder</div>;

  const isKaiCredit = refund.refundType === "kai_credit";

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p
              className="text-xl font-bold text-slate-800"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {refund.refundReference}
            </p>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                REFUND_STATUS_COLORS[refund.refundStatus] ||
                "bg-slate-100 text-slate-500"
              }`}
            >
              {REFUND_STATUS_LABELS[refund.refundStatus]}
            </span>
          </div>
          <div className="text-right">
            <p
              className={`text-2xl font-bold ${
                isKaiCredit ? "text-[rgb(var(--brand-rgb))]" : "text-amber-600"
              }`}
            >
              {fmtMoney(refund.refundAmount, refund.currency)}
            </p>
            <p className="text-xs text-slate-400">{refund.currency || "USD"}</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {refund.refundStatus !== "completed" && (
            <Btn
              variant="secondary"
              size="sm"
              loading={actionSaving}
              onClick={() => onStatusChange("completed")}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Mark Completed
            </Btn>
          )}
          {!refund.isClosed && (
            <Btn
              variant="secondary"
              size="sm"
              loading={actionSaving}
              onClick={() => onStatusChange("in_progress")}
            >
              Mark In Progress
            </Btn>
          )}
          {/* <Btn variant="secondary" size="sm" onClick={onSend}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Send
          </Btn> */}
          <Btn variant="ghost" size="sm" onClick={onEdit}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </Btn>
          {onAddTask && (
            <Btn variant="ghost" size="sm" onClick={onAddTask}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Add Task
            </Btn>
          )}
          {onDelete && (
            <Btn variant="ghost" size="sm" onClick={onDelete}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2">
                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-rose-500">Delete</span>
            </Btn>
          )}
          {/* <Btn variant="ghost" size="sm" onClick={handleDownload}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            Download
          </Btn> */}
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Client Info */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
              Client
            </p>
            <p className="font-bold text-slate-800">{refund.clientName}</p>
            <p className="text-sm text-slate-500">{refund.email}</p>
            {refund.phone && (
              <p className="text-sm text-slate-500">{refund.phone}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
              Trip
            </p>
            {refund.destination && (
              <p className="text-sm font-semibold text-slate-700">
                {refund.destination}
              </p>
            )}
            {refund.country && (
              <p className="text-sm text-slate-500 mt-0.5">{refund.country}</p>
            )}
          </div>
        </div>

        {/* Refund Summary */}
        <div className="rounded-xl overflow-hidden border border-slate-100">
          <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Refund Summary
            </p>
            <span className="text-[10px] font-bold text-slate-400">
              {refund.currency || "USD"}
            </span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-slate-100">
            <div className="p-4">
              <p className="text-xs text-slate-400 mb-1">Original Amount</p>
              <p className="text-base font-bold text-slate-800">
                {fmtMoney(refund.originalAmount, refund.currency)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-400 mb-1">Refund Amount</p>
              <p
                className={`text-base font-bold ${
                  isKaiCredit ? "text-[rgb(var(--brand-rgb))]" : "text-amber-600"
                }`}
              >
                {fmtMoney(refund.refundAmount, refund.currency)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-400 mb-1">Status</p>
              <p className="text-base font-bold text-slate-700">
                {REFUND_STATUS_LABELS[refund.refundStatus]}
              </p>
              {refund.isClosed && (
                <p className="text-[10px] text-emerald-600 mt-1">✓ Closed</p>
              )}
            </div>
          </div>
        </div>

        {/* Kai Credit Specific */}
        {isKaiCredit && (
          <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-widest mb-2">{`${BRAND.creditName} Details`}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400">Credit Applied</p>
                <p
                  className={`font-semibold ${
                    refund.kaiCreditApplied
                      ? "text-emerald-600"
                      : "text-slate-700"
                  }`}
                >
                  {refund.kaiCreditApplied ? "✓ Yes" : "✗ No"}
                </p>
              </div>
              {refund.kaiCreditApplied && refund.kaiCreditAppliedDate && (
                <div>
                  <p className="text-xs text-slate-400">Applied Date</p>
                  <p className="font-semibold text-slate-700">
                    {fmtD(refund.kaiCreditAppliedDate)}
                  </p>
                </div>
              )}
              {refund.kaiCreditDestination && (
                <div className="col-span-2">
                  <p className="text-xs text-slate-400">Credit Destination</p>
                  <p className="font-semibold text-slate-700">
                    {refund.kaiCreditDestination}
                  </p>
                </div>
              )}
              {refund.amountApplied != null && (
                <div>
                  <p className="text-xs text-slate-400">Amount Applied</p>
                  <p className="font-semibold text-slate-700">
                    {fmtMoney(refund.amountApplied, refund.currency)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment/Refund Method */}
        {(refund.refundMethod || refund.transactionReference) && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">
              Refund Details
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {refund.refundMethod && (
                <div>
                  <p className="text-xs text-slate-400">Method</p>
                  <p className="font-semibold text-slate-700">
                    {refund.refundMethod}
                  </p>
                </div>
              )}
              {refund.transactionReference && (
                <div>
                  <p className="text-xs text-slate-400">Transaction Ref</p>
                  <p className="font-semibold text-slate-700 font-mono">
                    {refund.transactionReference}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bank Info */}
        {refund.bankAccountNumber && (
          <div className="border border-slate-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
              Bank Account
            </p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400">Account No.</p>
                <p className="font-semibold text-slate-700">
                  {refund.bankAccountNumber}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Account Name</p>
                <p className="font-semibold text-slate-700">
                  {refund.bankAccountName}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Bank</p>
                <p className="font-semibold text-slate-700">
                  {refund.bankName}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          {refund.refundRequestDate && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-[10px] text-slate-400 uppercase">Requested</p>
              <p className="text-sm font-semibold text-slate-700">
                {fmtD(refund.refundRequestDate)}
              </p>
            </div>
          )}
          {refund.expectedRefundDate && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-[10px] text-slate-400 uppercase">Expected</p>
              <p className="text-sm font-semibold text-slate-700">
                {fmtD(refund.expectedRefundDate)}
              </p>
            </div>
          )}
          {refund.refundProcessedDate && (
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-[10px] text-emerald-600 uppercase">Processed</p>
              <p className="text-sm font-semibold text-emerald-700">
                {fmtD(refund.refundProcessedDate)}
              </p>
            </div>
          )}
          {refund.closedDate && (
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-[10px] text-slate-500 uppercase">Closed</p>
              <p className="text-sm font-semibold text-slate-700">
                {fmtD(refund.closedDate)}
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        {refund.reasonForRefund && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">
              Reason for Refund
            </p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {refund.reasonForRefund}
            </p>
          </div>
        )}

        {refund.internalNotes && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
              Internal Notes
            </p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {refund.internalNotes}
            </p>
          </div>
        )}

        {refund.clientNotes && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">
              Client Notes
            </p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {refund.clientNotes}
            </p>
          </div>
        )}

        {/* Tasks linked to this refund */}
        {(tasks.length > 0 || onAddTask) && (
          <div className="border border-slate-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Tasks {tasks.length > 0 && `(${tasks.length})`}
              </p>
              {onAddTask && (
                <button
                  onClick={onAddTask}
                  className="text-[11px] font-bold text-[rgb(var(--brand-rgb))] hover:underline"
                >
                  + Add task
                </button>
              )}
            </div>
            {tasks.length === 0 ? (
              <p className="text-xs text-slate-400">No tasks yet for this refund.</p>
            ) : (
              <div className="space-y-2">
                {tasks.map((t) => (
                  <div key={t._id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        t.priority === "high" ? "bg-rose-500" : t.priority === "medium" ? "bg-amber-400" : "bg-emerald-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{t.taskTitle}</p>
                      {t.dueDate && <p className="text-[10px] text-slate-400">{fmtD(t.dueDate)}</p>}
                    </div>
                    <span className="text-[10px] font-semibold text-slate-400 capitalize">
                      {String(t.status || "").replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}